import type { HTMLAttributes } from 'react';
import { cx } from '../lib/cx';

export interface BrandLoaderProps extends HTMLAttributes<HTMLSpanElement> {
  /** Width and height in px. Default 40. */
  size?: number;
  label?: string;
  /** Render on a dark/inverse surface: the bars take the dark-logo palette. */
  inverse?: boolean;
  /** Fill the parent and centre the mark — the whole-page / whole-section
   *  loading state, in place of a `center-screen` wrapper. */
  fill?: boolean;
}

/**
 * The logo's five bars rising and falling like a voice level meter — the
 * branded loader for page and section loads. Bar geometry matches `Logo`; the
 * `Spinner` stays the loader for buttons and inline text, where a 16px mark
 * would be unreadable.
 */
export function BrandLoader({ size = 40, label = 'Loading', inverse = false, fill = false, className, ...rest }: BrandLoaderProps) {
  return (
    <span
      className={cx('tc-brand-loader', inverse && 'tc-brand-loader--inverse', fill && 'tc-brand-loader--fill', className)}
      role="status"
      aria-label={label}
      {...rest}
    >
      {/* Square box (bars are 41 wide, 44 tall) so it centres like a spinner. */}
      <svg className="tc-brand-loader__mark" width={size} height={size} viewBox="-1.5 0 44 44" aria-hidden focusable="false">
        <rect x="0" y="15" width="5" height="14" rx="2.5" />
        <rect x="9" y="7" width="5" height="30" rx="2.5" />
        <rect className="tc-brand-loader__accent" x="18" y="0" width="5" height="44" rx="2.5" />
        <rect x="27" y="10" width="5" height="24" rx="2.5" />
        <rect x="36" y="16" width="5" height="12" rx="2.5" />
      </svg>
    </span>
  );
}
